import type { KeyboardEvent } from 'react';
import { BILLING_PLANS } from 'definitions';
import { BILLING_PLAN_CATALOGUE, type BillingInterval, type BillingPlan } from 'definitions/billing-plans';
import { cn } from 'helpers/cn';
import { formatEuro, formatUnits } from '@/state/billing/billing-format';
import { FOCUS_RING, MICRO_LABEL_SHAPE } from '../../ConsolePanel';
import { monthlyEquivalent, POPULAR_PLAN } from '@/state/onboarding/plan-model';

type PlanCardsProps = {
  /** The card that is picked, or none yet. */
  selected: BillingPlan | null;
  /** The plan the slider recommends ("Your fit"); only the sign-up has a slider. */
  recommended?: BillingPlan;
  /** The plan the account is on, at the interval on show ("Current"). */
  current?: BillingPlan | null;
  /** The plan a change to is waiting for the period end ("Scheduled"). */
  scheduled?: BillingPlan | null;
  interval: BillingInterval;
  onSelect: (plan: BillingPlan) => void;
};

/**
 * The five plans as a radio group. Arrow keys move the selection (and focus), and only the
 * selected card is in the tab order, as a radio group is.
 */
export const PlanCards = ({ selected, recommended, current, scheduled, interval, onSelect }: PlanCardsProps) => {
  const move = (ev: KeyboardEvent<HTMLDivElement>, index: number) => {
    const step =
      ev.key === 'ArrowRight' || ev.key === 'ArrowDown' ? 1 : ev.key === 'ArrowLeft' || ev.key === 'ArrowUp' ? -1 : 0;
    if (!step) return;
    ev.preventDefault();
    const next = (index + step + BILLING_PLANS.length) % BILLING_PLANS.length;
    const plan = BILLING_PLANS[next];
    if (!plan) return;
    onSelect(plan);
    (ev.currentTarget.parentElement?.children[next] as HTMLElement | undefined)?.focus();
  };

  return (
    <div
      role="radiogroup"
      aria-label="Plans"
      className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,150px),1fr))] gap-2.5"
    >
      {BILLING_PLANS.map((plan, index) => {
        const details = BILLING_PLAN_CATALOGUE[plan];
        const on = plan === selected;
        const perMonth = monthlyEquivalent({ plan, interval });
        return (
          <div
            key={plan}
            role="radio"
            aria-checked={on}
            tabIndex={on || (!selected && index === 0) ? 0 : -1}
            onClick={() => onSelect(plan)}
            onKeyDown={(ev) => move(ev, index)}
            className={cn(
              'hover:border-accent flex min-w-0 cursor-pointer flex-col gap-3 rounded-xl border px-3.5 pt-4 pb-3.5 transition-colors duration-150',
              FOCUS_RING,
              on ? 'bg-sel-bg border-sel-line' : 'bg-panel2 border-line',
            )}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-foreground text-[13px] font-medium">{details.name}</span>
              <span
                className={cn(
                  'flex size-4 flex-none items-center justify-center rounded-full',
                  on ? 'border-sel border-[1.5px]' : 'border-input border',
                )}
              >
                <span className={cn('size-2 rounded-full', on ? 'bg-sel' : 'bg-transparent')} />
              </span>
            </div>

            <div className="flex min-h-5 flex-wrap gap-1.5">
              {plan === current && (
                <span
                  className={cn(
                    MICRO_LABEL_SHAPE,
                    'border-ok-line text-ok rounded-full border px-2 py-[3px] tracking-[0.08em] whitespace-nowrap',
                  )}
                >
                  Current
                </span>
              )}
              {plan === scheduled && (
                <span
                  className={cn(
                    MICRO_LABEL_SHAPE,
                    'border-sel-line text-sel rounded-full border px-2 py-[3px] tracking-[0.08em] whitespace-nowrap',
                  )}
                >
                  Next period
                </span>
              )}
              {plan === recommended && (
                <span
                  className={cn(
                    MICRO_LABEL_SHAPE,
                    'bg-accent rounded-full px-2 py-1 tracking-[0.08em] whitespace-nowrap text-white',
                  )}
                >
                  Your fit
                </span>
              )}
              {plan === POPULAR_PLAN && plan !== current && plan !== scheduled && (
                <span
                  className={cn(
                    MICRO_LABEL_SHAPE,
                    'border-sel-line text-sel rounded-full border px-2 py-[3px] tracking-[0.08em] whitespace-nowrap',
                  )}
                >
                  Most picked
                </span>
              )}
            </div>

            <div className="flex flex-col gap-[3px]">
              <div className="flex items-baseline gap-[5px] whitespace-nowrap">
                <span className="metric-value text-foreground">{formatEuro(perMonth)}</span>
                <span className="micro-label tracking-[0.06em]">/mo</span>
              </div>
              <div className="text-dim text-nano flex gap-[5px] overflow-hidden whitespace-nowrap">
                {interval === 'year' && (
                  <span className="flex-none line-through">{formatEuro(details.monthlyPrice)}</span>
                )}
                <span className="overflow-hidden text-ellipsis">
                  {interval === 'year' ? 'yearly, excl. VAT' : 'monthly, excl. VAT'}
                </span>
              </div>
            </div>

            <div className="border-line flex flex-col gap-1.5 border-t pt-3">
              <span className="text-foreground text-micro leading-snug">
                {details.sessionsPerMonth === null
                  ? 'Unlimited sessions'
                  : `${formatUnits(details.sessionsPerMonth)} sessions a month`}
              </span>
              <span className="text-dim text-nano leading-snug">
                {formatUnits(details.aiCreditsPerMonth)} AI credits a month
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};
